import { type InitialRulesValues } from 'contexts/RulesDetailModalContext';
import { Formik } from 'formik';
import { useCallback, useMemo, useRef } from 'react';

import {
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  type Rule,
  type Resolution,
  LOGICAL_OPERATORS,
  type Signal,
  useRulesJsonValidate,
  getErrorMessage,
} from '@frontend/api';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  useToast,
} from '@frontend/components';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import { ImportResultCategory } from '../types/rulesImport';
import {
  type SignalWithId,
  addIdsToSignal,
  stripIdsFromSignal,
} from '../utils/queryBuilding';
import {
  type ResolutionWithId,
  addIdsToResolution,
  stripIdsFromResolution,
} from '../utils/resolutions';
import { validateRule } from '../utils/validation';
import { RulesDetailModalContent } from './RulesDetailModalContent';

const isSignalWithId = (
  signal: Signal | SignalWithId,
): signal is SignalWithId => {
  return 'id' in signal;
};

// Form Data Types
type FormRule = Omit<Rule, 'uuid' | 'signals' | 'resolutions'>;
type FormPayloadData = FormRule & {
  signals: Signal[];
  resolutions: Resolution[];
};

export type FormStateData = FormRule & {
  signals: SignalWithId[];
  resolutions: ResolutionWithId[];
};

const FORM_INITIAL_VALUES: FormStateData = {
  name: '',
  description: '',
  logicalOperator: LOGICAL_OPERATORS.AND,
  signals: [],
  resolutions: [],
};

type RulesDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialRule?: InitialRulesValues;
  mode: 'create' | 'update' | 'validate';
  isReadOnly: boolean;
  showConfigureRulesButton?: boolean;
  onSuccess?: (data: Rule, category?: ImportResultCategory) => void;
  onDeleteRule?: (data: Rule) => void;
};

export const RulesDetailModal = ({
  isOpen,
  onClose,
  initialRule,
  mode,
  isReadOnly,
  showConfigureRulesButton,
  onSuccess,
  onDeleteRule,
}: RulesDetailModalProps) => {
  // Get Loading State for Config
  const { isLoading: isLoadingConfig, resolutionConfig } =
    useReqsReviewConfigContext();

  const toast = useToast();

  // Create Rule Mutation
  const {
    mutate: createRule,
    isLoading: isCreatingRule,
    error: createRuleError,
  } = useCreateRule({
    queryOptions: {
      onSuccess: (data) => {
        onClose();
        // If we have a success callback, call it with the success data
        onSuccess?.(data);
      },
      onError: (error) => {
        toast({
          status: 'error',
          title: 'Failed to create rule',
          description: getErrorMessage(error),
        });
      },
    },
  });

  // Update Rule Mutation
  const {
    mutate: updateRule,
    isLoading: isUpdatingRule,
    error: updateRuleError,
  } = useUpdateRule({
    queryOptions: {
      onSuccess: (data) => {
        onClose();
        // If we have a success callback, call it with the success data
        onSuccess?.(data);
      },
      onError: (error) => {
        toast({
          status: 'error',
          title: 'Failed to update rule',
          description: getErrorMessage(error),
        });
      },
    },
  });

  // Validate rule mutation
  const {
    mutate: validateRuleMutation,
    isLoading: isValidatingRule,
    error: validateRuleError,
  } = useRulesJsonValidate({
    queryOptions: {
      onSuccess: (data) => {
        if (data.errors.length > 0) {
          // NOTE: assume length one since details modal
          toast({
            status: 'error',
            title: 'Rule is invalid',
            description: data.errors[0].errors.map((e) => e.message).join(', '),
          });
        } else {
          onClose();
          const category = data.created.length > 0 ? 'created' : 'updated';
          const out =
            data.created.length > 0
              ? data.created[0].rule
              : data.updated.length > 0
              ? data.updated[0].rule
              : null;
          if (out) {
            onSuccess?.(out, category);
          }
        }
      },
      onError: (error) => {
        toast({
          status: 'error',
          title: 'Failed to validate rule',
          description: getErrorMessage(error),
        });
      },
    },
  });

  // Type guard to check if initialRule is a complete Rule
  const isCompleteRule = (
    rule: InitialRulesValues | undefined,
  ): rule is Rule => {
    return (
      rule !== undefined && 'uuid' in rule && typeof rule.uuid === 'string'
    );
  };

  // Delete rule mutation
  const { mutate: deleteRule, isLoading: isDeletingRule } = useDeleteRule(
    { uuid: initialRule?.uuid ?? '' },
    {
      queryOptions: {
        onSuccess: () => {
          onClose();
          if (isCompleteRule(initialRule)) {
            onDeleteRule?.(initialRule);
          }
        },
      },
    },
  );

  // Consolidate the query data and state for which action we're taking
  const { mutate, isLoading, mutationError, submitButtonText } = useMemo(() => {
    if (mode === 'update' && isCompleteRule(initialRule)) {
      return {
        // Pack uuid into update rule call
        mutate: (formData: FormPayloadData) =>
          updateRule({ uuid: initialRule.uuid, ...formData }),
        isLoading: isUpdatingRule || isDeletingRule,
        mutationError: updateRuleError,
        submitButtonText: 'Update Rule',
      };
    } else if (mode === 'create') {
      return {
        mutate: createRule,
        isLoading: isCreatingRule || isDeletingRule,
        mutationError: createRuleError,
        submitButtonText: 'Create Rule',
      };
    } else {
      return {
        mutate: (formData: FormPayloadData) =>
          validateRuleMutation({ rulesData: [formData] }),
        isLoading: isValidatingRule,
        mutationError: validateRuleError,
        submitButtonText: 'Validate Rule',
      };
    }
  }, [
    mode,
    initialRule,
    createRule,
    createRuleError,
    isCreatingRule,
    updateRule,
    updateRuleError,
    isUpdatingRule,
    isDeletingRule,
    validateRuleMutation,
    validateRuleError,
    isValidatingRule,
  ]);

  // Create memoized validation function so we can map resolution parameters to their respective validation schemas
  const validate = useCallback(
    (values: FormStateData) =>
      resolutionConfig ? validateRule(values, resolutionConfig) : undefined,
    [resolutionConfig],
  );

  const initialValues = useMemo(() => {
    const initialSignals: SignalWithId[] = [];
    if (initialRule && initialRule.signals) {
      initialRule.signals.forEach((signal) => {
        if (isSignalWithId(signal)) {
          initialSignals.push(JSON.parse(JSON.stringify(signal)));
        } else {
          initialSignals.push(addIdsToSignal(signal));
        }
      });
    }
    return {
      ...FORM_INITIAL_VALUES,
      ...initialRule,
      signals: initialSignals,
      resolutions: initialRule
        ? initialRule.resolutions?.map(addIdsToResolution) ?? []
        : [],
    };
  }, [initialRule]);

  const handleSubmit = (formData: FormStateData) => {
    // Strip IDs and empty groups and signals before mutation
    // add missing countQuery values
    const cleanedSignals: Signal[] = [];
    formData.signals.forEach((signal) => {
      const strippedSignal = stripIdsFromSignal(signal);
      const nextGroups = strippedSignal.groups
        .filter((group) => group.queries.length > 0 || group.countQuery)
        .map((group) => ({
          ...group,
          countQuery: group.countQuery ?? null,
        }));
      if (nextGroups.length > 0) {
        cleanedSignals.push({
          ...strippedSignal,
          groups: nextGroups,
        });
      }
    });
    mutate({
      ...formData,
      signals: cleanedSignals,
      resolutions: formData.resolutions.map(stripIdsFromResolution),
    });
  };

  const initialFocusRef = useRef<HTMLInputElement>(null);

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialFocusRef}
    >
      <ModalContent
        height="max-content"
        maxHeight="100%"
        overflow="hidden"
        marginY={0}
      >
        <ModalHeader>
          {mode === 'create'
            ? 'Create Rule'
            : mode === 'update'
            ? 'Edit Rule'
            : 'Edit Rule'}
          <ModalCloseButton />
        </ModalHeader>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validate={validate}
          validateOnBlur
          onSubmit={handleSubmit}
        >
          <RulesDetailModalContent
            isLoadingConfig={isLoadingConfig}
            isReadOnly={isReadOnly}
            mutationError={mutationError}
            isMutating={isLoading}
            resolutionConfigMapped={resolutionConfig?.mapped ?? null}
            initialRule={initialRule}
            showConfigureRulesButton={showConfigureRulesButton}
            submitButtonText={submitButtonText}
            onClose={onClose}
            deleteRule={deleteRule}
            mode={mode}
            initialFocusRef={initialFocusRef}
          />
        </Formik>
      </ModalContent>
    </Modal>
  );
};
